import { absBig, BIG_ONE, BIG_ZERO } from "@/lib/tool-kit/math/bigint";
import { toSuperscript } from "@/lib/tool-kit/math/primes";
import {
  addR,
  equalsR,
  formatFraction,
  fromBigInt,
  intR,
  isZeroR,
  mulR,
  parseRational,
  type Rational,
  signR,
} from "@/lib/tool-kit/math/rational";
import { rationalPow } from "@/lib/tool-kit/number-theory/geometric-sequence";

/**
 * Expanding (a + b)ⁿ for terms of the form c·vᵖ — a rational coefficient, an optional
 * single-letter variable and an optional whole-number power, such as 2x, −3y², x⁻¹, 1/2.
 *
 * Term k (counting from 0) is C(n, k)·aⁿ⁻ᵏ·bᵏ. Coefficients are exact rationals, binomial
 * coefficients are BigInt, and terms whose variable powers coincide are combined.
 */

export const MAX_POWER = 100;
export const MAX_TERM_EXPONENT = 20;
export const MAX_TOKEN_DIGITS = 30;

export interface Term {
  coefficient: Rational;
  variable: string | null;
  /** The power of the variable (0 when there is none). */
  power: number;
}

export type TermRead =
  | { kind: "ok"; term: Term }
  | { kind: "empty" | "invalid" | "too-long" | "big-power" };

const TERM_PATTERN =
  /^([+-]?)\s*(?:\((-?[\d./]+)\)|([\d./]+))?\s*\*?\s*(?:([a-zA-Z])(?:\^\(?([+-]?\d+)\)?)?)?$/;
const DIGIT_RUN = /\d+/g;
const MINUS_SIGN = /−/g;
const SPACES = /\s+/g;

function termPower(
  variable: string | undefined,
  exponent: string | undefined
): number {
  if (variable === undefined) {
    return 0;
  }
  return exponent === undefined ? 1 : Number(exponent);
}

/** Read one term of the binomial: "2x", "-3y^2", "x^-1", "(1/2)x", "5". */
export function parseTerm(raw: string): TermRead {
  const text = raw.replace(MINUS_SIGN, "-").trim();
  if (text === "") {
    return { kind: "empty" };
  }
  const longest = Math.max(
    0,
    ...(text.match(DIGIT_RUN) ?? []).map((run) => run.length)
  );
  if (longest > MAX_TOKEN_DIGITS) {
    return { kind: "too-long" };
  }
  const match = TERM_PATTERN.exec(text.replace(SPACES, " "));
  if (match === null) {
    return { kind: "invalid" };
  }
  const [, sign, parenthesised, bare, variable, exponent] = match;
  const coefficientText = parenthesised ?? bare;
  if (coefficientText === undefined && variable === undefined) {
    return { kind: "invalid" };
  }
  const magnitude =
    coefficientText === undefined ? intR(1) : parseRational(coefficientText);
  if (magnitude === null) {
    return { kind: "invalid" };
  }
  const power = termPower(variable, exponent);
  if (Math.abs(power) > MAX_TERM_EXPONENT) {
    return { kind: "big-power" };
  }
  const negative = sign === "-";
  return {
    kind: "ok",
    term: {
      coefficient: negative
        ? { num: -magnitude.num, den: magnitude.den }
        : magnitude,
      variable: variable ?? null,
      power: power === 0 ? 0 : power,
    },
  };
}

/** C(n, k) as a BigInt, by the multiplicative formula. */
export function binomialCoefficient(n: number, k: number): bigint {
  if (k < 0 || k > n) {
    return BIG_ZERO;
  }
  const r = Math.min(k, n - k);
  let result = BIG_ONE;
  for (let i = 1; i <= r; i += 1) {
    result = (result * BigInt(n - r + i)) / BigInt(i);
  }
  return result;
}

export type Powers = Record<string, number>;

export interface ExpansionTerm {
  /** Position of the first term it came from, counting from 0. */
  k: number;
  binomial: bigint;
  coefficient: Rational;
  powers: Powers;
}

export interface Expansion {
  /** Every term k = 0 … n, uncombined, in order. */
  parts: ExpansionTerm[];
  /** Terms with identical variable powers combined, zero terms dropped. */
  terms: ExpansionTerm[];
}

function powersAt(a: Term, b: Term, n: number, k: number): Powers {
  const powers: Powers = {};
  const add = (term: Term, times: number) => {
    if (term.variable !== null && term.power !== 0) {
      powers[term.variable] = (powers[term.variable] ?? 0) + term.power * times;
    }
  };
  add(a, n - k);
  add(b, k);
  for (const name of Object.keys(powers)) {
    if (powers[name] === 0) {
      delete powers[name];
    }
  }
  return powers;
}

function powersKey(powers: Powers): string {
  return Object.keys(powers)
    .sort()
    .map((name) => `${name}^${powers[name]}`)
    .join(",");
}

/** (a + b)ⁿ term by term, with like terms combined. */
export function expand(a: Term, b: Term, n: number): Expansion {
  const parts: ExpansionTerm[] = [];
  for (let k = 0; k <= n; k += 1) {
    const binomial = binomialCoefficient(n, k);
    const coefficient = mulR(
      mulR(fromBigInt(binomial), rationalPow(a.coefficient, n - k)),
      rationalPow(b.coefficient, k)
    );
    parts.push({ k, binomial, coefficient, powers: powersAt(a, b, n, k) });
  }
  const combined = new Map<string, ExpansionTerm>();
  for (const part of parts) {
    const key = powersKey(part.powers);
    const existing = combined.get(key);
    combined.set(
      key,
      existing
        ? {
            ...existing,
            coefficient: addR(existing.coefficient, part.coefficient),
          }
        : part
    );
  }
  return {
    parts,
    terms: [...combined.values()].filter((term) => !isZeroR(term.coefficient)),
  };
}

/** The sum of the coefficients: every variable set to 1, which is (ca + cb)ⁿ. */
export function coefficientSum(expansion: Expansion): Rational {
  return expansion.terms.reduce(
    (total, term) => addR(total, term.coefficient),
    intR(0)
  );
}

/* ---------------------------------------------------------- formatting */

/** "x", "x²", "x⁻¹", "xy³": the variable part of a term. */
export function formatPowers(powers: Powers): string {
  return Object.keys(powers)
    .sort()
    .map((name) =>
      powers[name] === 1 ? name : `${name}${toSuperscript(powers[name])}`
    )
    .join("");
}

export type CoefficientFormatter = (value: Rational) => string;

/** One term of the expansion: "−6x²", "(3/4)x", "8". */
export function formatTerm(
  coefficient: Rational,
  powers: Powers,
  format: CoefficientFormatter = formatFraction
): string {
  const variables = formatPowers(powers);
  const negative = signR(coefficient) < 0;
  const magnitude = negative
    ? { num: -coefficient.num, den: coefficient.den }
    : coefficient;
  const sign = negative ? "−" : "";
  if (variables === "") {
    return `${sign}${format(magnitude)}`;
  }
  if (equalsR(magnitude, intR(1))) {
    return `${sign}${variables}`;
  }
  const body = format(magnitude);
  return `${sign}${magnitude.den === BIG_ONE ? body : `(${body})`}${variables}`;
}

/** The terms joined into one polynomial: "x³ − 6x² + 12x − 8". */
export function formatExpansion(
  terms: readonly ExpansionTerm[],
  format: CoefficientFormatter = formatFraction
): string {
  if (terms.length === 0) {
    return "0";
  }
  return terms
    .map((term, index) => {
      const text = formatTerm(term.coefficient, term.powers, format);
      if (index === 0) {
        return text;
      }
      return text.startsWith("−") ? ` − ${text.slice(1)}` : ` + ${text}`;
    })
    .join("");
}

/** A term of the binomial as typed, for display: "2x", "−3y²", "1/2". */
export function formatBinomialTerm(term: Term): string {
  return formatTerm(
    term.coefficient,
    term.variable !== null && term.power !== 0
      ? { [term.variable]: term.power }
      : {}
  );
}

/** Digits in the coefficient, for deciding whether to abbreviate it. */
export function coefficientDigits(coefficient: Rational): number {
  return Math.max(
    absBig(coefficient.num).toString().length,
    coefficient.den.toString().length
  );
}

/* ------------------------------------------------------------- finding */

/** The first variable in a or b, or null when both are numbers. */
export function mainVariable(a: Term, b: Term): string | null {
  if (a.variable !== null && a.power !== 0) {
    return a.variable;
  }
  return b.variable !== null && b.power !== 0 ? b.variable : null;
}

/** The term whose power of `variable` is p, or null if there is none. */
export function termWithPower(
  expansion: Expansion,
  variable: string,
  p: number
): ExpansionTerm | null {
  return (
    expansion.terms.find((term) => (term.powers[variable] ?? 0) === p) ?? null
  );
}

export interface TermBreakdown {
  binomial: bigint;
  /** aⁿ⁻ᵏ as a term, and bᵏ. */
  aPart: { coefficient: Rational; powers: Powers };
  bPart: { coefficient: Rational; powers: Powers };
  product: { coefficient: Rational; powers: Powers };
}

/** Term k (from 0) of (a + b)ⁿ, with its three factors. */
export function termBreakdown(
  a: Term,
  b: Term,
  n: number,
  k: number
): TermBreakdown {
  const binomial = binomialCoefficient(n, k);
  const powerOf = (term: Term, times: number): Powers =>
    term.variable !== null && term.power !== 0 && times !== 0
      ? { [term.variable]: term.power * times }
      : {};
  const aPart = {
    coefficient: rationalPow(a.coefficient, n - k),
    powers: powerOf(a, n - k),
  };
  const bPart = {
    coefficient: rationalPow(b.coefficient, k),
    powers: powerOf(b, k),
  };
  return {
    binomial,
    aPart,
    bPart,
    product: {
      coefficient: mulR(
        mulR(fromBigInt(binomial), aPart.coefficient),
        bPart.coefficient
      ),
      powers: powersAt(a, b, n, k),
    },
  };
}
